import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, maxDate, minDate } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = ma => `${ma.meetingid}_${ma.attendee}`;
const meetingFields = ['title', 'location', 'start', '"end"', {local: 'meeting_tz', foreign: 'tz'}];

export const meetingAttendeeStaffSlice = createSlice({
    name: 'staffMeetingAttendees',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.keys.forEach(k => delete state.data[key(k)]);
            action.payload.data.forEach(ma => state.data[key(ma)] = ma);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(ma => state.data[key(ma)] = ma);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(ma => delete state.data[key(ma)]);
        },
        updateMeeting: (state, action) => {
            ensureArray(action.payload).forEach(meeting => {
                const toUpdate = Object.keys(state.data).filter(key => key.startsWith(`${meeting.id}_`));
                toUpdate.forEach(key => {
                    const ma = state.data[key];
                    meetingFields.forEach(f => {
                        if (typeof f === 'string') {
                            ma[f] = meeting[f]
                        } else {
                            ma[f.local] = meeting[f.foreign]
                        }
                    });
                })
            });
        },
        removeMeeting: (state, action) => {
            ensureArray(action.payload).forEach(meetingid => {
                const toRemove = Object.keys(state.data).filter(key => key.startsWith(`${meetingid}_`));
                toRemove.forEach(key => delete state.data[key]);
            });
        },
    },
    extraReducers: builder => {
        builder.addCase(logout, () => meetingAttendeeStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'meetingAttendee', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'meetingAttendee');

const fetchMeetingAttendee = aggregatingFetch(
    (key) => network => network.post('/api/v2/staffMeetingAttendees', key, data => meetingAttendeeStaffSlice.actions.set({ keys: [key], data }),visualizeFailure('get') ),
    (keys) => network => network.post('/api/v2/staffMeetingAttendees', keys, data => meetingAttendeeStaffSlice.actions.set({ keys, data }), visualizeFailure('get'))
);
const fetchMemberMeetingsInRange = aggregatingFetch(
    (start, end) => network => network.get(`/api/v2/staffMeeting/member/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, meetingAttendeeStaffSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get(`/api/v2/staffMeeting/member/${minDate(keys, 0)}/${maxDate(keys, 1)}`, meetingAttendeeStaffSlice.actions.addOrUpdate, visualizeFailure('get'))
)

const fetchAttendeesByMeeting = aggregatingFetch(
    meetingid => network => network.post('/api/v2/staffMeetingAttendeesByMeeting', [meetingid], meetingAttendeeStaffSlice.actions.addOrUpdate, visualizeFailure('get')),
    meetingids => network => network.post('/api/v2/staffMeetingAttendeesByMeeting', meetingids, meetingAttendeeStaffSlice.actions.addOrUpdate, visualizeFailure('get'))
)
// #endregion

// #region selectors
const selectMeetingAttendees = state => state.staffMeetingAttendees.data;
const selectMemberMeetingsInRange = (start, end) => createSelector([selectMeetingAttendees], meetingAttendees => Object.values(meetingAttendees).filter(ma => dateRangeOverlap(ma, { start, end })));
const selectAttendeesByMeeting = meetingid => createSelector([selectMeetingAttendees], attendees => Object.values(attendees).filter(a => a.meetingid === meetingid));
// #endregion

const listen = {
    meetingAttendeeStaffAdded: (attendee, { dispatch, websocket }) => {
        markFetched(websocket, 'meeting_attendee', attendee.attendee, attendee.meetingid);
        dispatch(meetingAttendeeStaffSlice.actions.addOrUpdate(attendee))
    },
    meetingAttendeeStaffUpdated: (attendee, { dispatch }) => dispatch(meetingAttendeeStaffSlice.actions.addOrUpdate(attendee)),
    meetingAttendeeStaffRemoved: (attendee, { dispatch }) => dispatch(meetingAttendeeStaffSlice.actions.remove(attendee)),
    meetingUpdated: (meeting, { dispatch }) => dispatch(meetingAttendeeStaffSlice.actions.updateMeeting(meeting)),
    meetingRemoved: (meeting, { dispatch }) => dispatch(meetingAttendeeStaffSlice.actions.removeMeeting(meeting)),
}

const meetingAttendeeInitByID = initMapFactory(fetchMeetingAttendee, listen);
registerEntityBasedFetch('meeting_attendee', meetingAttendeeInitByID)
const options = {
    cleanup: { root: meetingAttendeeStaffSlice.name, all: state => Object.values(selectMeetingAttendees(state)), removeAll: meetingAttendeeStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member')
}
export const [useStaffMemberMeetingsInRange] = dataHookWithCredentialsFactory(selectMemberMeetingsInRange, queryMapFactory(fetchMemberMeetingsInRange, ['meetingid','attendee'], meetingAttendeeInitByID, listen), options);
export const [useStaffMeetingAttendeesByMeetingId] = dataHookWithCredentialsFactory(selectAttendeesByMeeting, queryMapFactory(fetchAttendeesByMeeting, ['meetingid','attendee'], meetingAttendeeInitByID, listen), options);

const reducer = { name: meetingAttendeeStaffSlice.name, reducer: meetingAttendeeStaffSlice.reducer }
export default reducer;